export type SimulationStep = 1 | 2 | 3 | 4 | 4.5 | 5 | 6 | 7 | 8;

export const COMPETENCE_WINDOW_ERROR_CODE = 'VIRADA_COMPETENCIA';

// Canal usado pelo banco para o cliente final autorizar a consulta de margem (ver modal
// de seleção de financeiras em Step1Form). Cadastrado por financeira no admin
// (SimulationFinancials) e retornado em simulation/screening/financials.
export type FinanceiraAuthChannel = 'TOKEN' | 'SMS' | 'LINK' | 'WHATSAPP' | 'SMS_WHATSAPP' | 'CONSULTA_AUTORIZADA';

export const FINANCEIRA_AUTH_CHANNEL_OPTIONS: { value: FinanceiraAuthChannel; label: string }[] = [
  { value: 'TOKEN', label: 'Token' },
  { value: 'SMS', label: 'SMS' },
  { value: 'LINK', label: 'Link Disponível' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'SMS_WHATSAPP', label: 'SMS/WhatsApp' },
  { value: 'CONSULTA_AUTORIZADA', label: 'Consulta Autorizada' },
];

export const FINANCEIRA_AUTH_CHANNEL_LABELS: Record<FinanceiraAuthChannel, string> = {
  TOKEN: 'Token',
  SMS: 'SMS',
  LINK: 'Link Disponível',
  WHATSAPP: 'WhatsApp',
  SMS_WHATSAPP: 'SMS/WhatsApp',
  CONSULTA_AUTORIZADA: 'Consulta Autorizada',
};

export interface FormDataCLT {
  nome_completo: string;
  cpf: string;
  data_nascimento: string;
  telefone: string;
  estado: string;
  cidade: string;
  alfabetizado?: string;
  mobilidade_reduzida?: string;
  sexo?: string;
  nacionalidade?: string;
  tempo_admissao?: number;
  data_admissao?: string;
  legado?: string;
  data_inicio_cnpj?: string;
  quantidade_funcionarios?: number;
  tipo_produto?: string;
  parcela_minima?: number;
  // Canonical_ids das financeiras escolhidas no modal de autorização (Soma já expandido
  // em SOMA_UY3/SOMA_CELCOIN — ver FinanceirasConsentModal). Ausente/undefined quando o
  // modal ainda não foi aceito.
  financeiras?: string[];
}

// Retorno de simulation/screening/cpf/{cpf} — dados cadastrais usados para autopreencher
// a etapa "Dados do cliente para simulação". Todos os campos são opcionais: o
// enriquecimento é conveniência, nunca dependência (ver AI-1204).
export interface ClienteScreening {
  cpf?: string;
  nome_completo?: string;
  data_nascimento?: string;
  telefone_celular?: string;
  estado?: string;
  cidade?: string;
}

// Última simulação já feita para o cliente, devolvida por simulation/screening/consult
// (SB-1953). As chaves vêm em MAIÚSCULAS: este endpoint manteve o padrão que já tinha, ao
// contrário do formulário de digitação (minúsculas) — a divergência entre os dois é
// proposital, não um deslize a corrigir de um lado só.
export interface UltimaSimulacao {
  DATA_SIMULACAO?: string;
  MODO?: string;
  QTDE_PARCELAS?: number;
  // Só o eixo efetivamente simulado é devolvido — VALOR_SOLICITADO para VALOR_DESEJADO/
  // VALOR_TOTAL, VALOR_PARCELA para VALOR_PARCELA; o outro é omitido. Ausência aqui é
  // "não se aplica", nunca zero.
  VALOR_SOLICITADO?: number;
  VALOR_PARCELA?: number;
  // Jornada da simulação anterior — diferente do JOURNEY_ID da raiz, que é a nova.
  JOURNEY_ID?: string;
}

// Corpo de `data` em simulation/screening/consult a partir do SB-1953 (antes era só a
// string do journey_id).
export interface ScreeningConsultData {
  JOURNEY_ID?: string;
  STATUS?: string;
  FINANCEIRAS_ALVO?: string[];
  // Ausente só quando o integrador ainda não subiu o SB-1953 — `false` é a informação
  // "cliente novo", não falta de informação. O backend repassa o que recebe, sem default.
  CLIENTE_CONHECIDO?: boolean;
  // Omitido (a chave não existe, não vem null) quando o cliente não tem histórico.
  ULTIMA_SIMULACAO?: UltimaSimulacao;
}

// Cadastro do cliente que acompanha cada linha do histórico — alimenta o
// pré-preenchimento do formulário ao reabrir a simulação pelo histórico. TELEFONE vem
// só com os dígitos e ESTADO como sigla (ver handleSimularDoHistorico).
export interface HistoricoDadosCliente {
  NOME?: string;
  DATA_NASCIMENTO?: string;
  TELEFONE?: string;
  ESTADO?: string;
  CIDADE?: string;
}

// Item de simulation/screening/history — uma linha POR CLIENTE (a última simulação de
// cada um), já ordenada por recência pelo backend; não é a listagem de todas as
// simulações. A chamada não envia identificador de operador: o backend resolve parceiro e
// dependente pelo usuário logado, o que impede pedir o histórico de outro operador.
export interface HistoricoSimulacaoItem {
  CPF_CLIENTE: string;
  LOGGED_PARTNER_ID?: string;
  // Omitido quando a simulação foi feita pelo próprio parceiro, sem dependente.
  LOGGED_DEPENDENT_ID?: string;
  DADOS_CLIENTE?: HistoricoDadosCliente;
  // Mesma forma (e mesmas regras de omissão) do histórico devolvido pelo /consult.
  SIMULACAO?: UltimaSimulacao;
}

export interface StateOption {
  value: string;
  label: string;
  uf: string;
}

export interface CityOption {
  value: string;
  label: string;
}

export type FinanceiraStatus =
  | 'PRE_ANALISE'
  | 'AGUARDANDO_AUTORIZACAO'
  | 'AUTORIZADO'
  | 'AGUARDANDO_CODIGO'
  | 'CONCLUIDO'
  | 'ERRO';

// Financeira ativa retornada por simulation/screening/financials — usada para popular o
// select de financeira na Consulta de Propostas por número (ver SimulationBank.tsx).
export interface FinanceiraOption {
  id: number;
  name: string;
  logo_url: string | null;
  canonical_id: string;
  is_active: boolean;
  // Canal de autorização do cliente final (ver FinanceirasConsentModal) — string solta
  // (não FinanceiraAuthChannel) porque o backend valida por enum e pode devolver algo
  // além dos 4 valores conhecidos hoje; o front trata desconhecido como "sem badge".
  auth_channel?: string | null;
}

export interface FinanceiraCard {
  id: number | string;
  canonical_id?: string;
  nome: string;
  logo: string;
  logoMobile?: string;
  probabilidade: 'alta' | 'baixa' | 'disponivel';
  status: FinanceiraStatus;
  link_url?: string;
  // Quando true, o banco já autorizou a consulta antes de disponibilizar o link SCR
  // (ex.: consulta de sistema chega autorizada antes do pre_analise) — nesse caso o
  // backend não envia link_url e o front deve mostrar o status já autorizado.
  already_authorized?: boolean;
  valor?: number;
  prazo_min?: number;
  prazo_max?: number;
  parcela_min?: number;
  parcela_max?: number;
  erro?: string;
  selecionada: boolean;
  hidden?: boolean;
  mensagem_tratada?: string | null;
}

export interface SimulacaoParams {
  valor_desejado?: number;
  valor_parcela?: number;
  num_parcelas?: number;
  financeiras: (number | string)[];
}

export interface SimulacaoResult {
  financeira_id: number | string;
  financeira_nome: string;
  financeira_logo: string;
  qtde_parcelas?: number;
  valor_solicitado?: number;
  valor_parcela?: number;
  primeiro_vencimento?: string;
  ultimo_vencimento?: string;
  iof?: number;
  cet_mensal?: number;
  cet_anual?: number;
  valor_total_cet?: number;
  taxa_inicial?: number;
  taxa_final?: number;
  comissao_total?: number;
  comissao_vista?: number;
  seguro?: string | boolean;
  erro?: string;
}

// Oferta escolhida em Step4Results para prosseguir com a digitação da proposta.
export interface OfertaSelecionada {
  key: string;
  id: string;
  financeira: string;
  qtde_parcelas?: number;
  valor_solicitado?: number;
  valor_parcela?: number;
  primeiro_vencimento?: string;
  seguro?: string | boolean;
  valor_seguro?: number;
  descricao_seguro?: string;
  codigo_domus?: string;
  codigo_banco?: string | number;
  descricao_convenio?: string;
}

// Formulário de digitação: dirigido pelo schema retornado por simulation/digitations/form,
// que varia por financeira. Os valores são armazenados como objeto aninhado, um nível por
// segmento da "chave" (ex.: "dados_cliente.bancarios.agencia" -> { dados_cliente: { bancarios: { agencia } } }).
export type FormDataDigitacao = Record<string, any>;

export interface DigitacaoFormFieldOption {
  innerText: string;
  value: string;
}

export interface DigitacaoFormFieldCombo {
  tipo: string;
  dinamico: boolean;
  parametros: string[];
}

export interface DigitacaoFormField {
  chave: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'number' | 'select';
  required: boolean;
  placeholder?: string;
  pattern?: string;
  title?: string;
  option?: DigitacaoFormFieldOption[];
  combo?: DigitacaoFormFieldCombo;
  // SB-1953 — valor da última digitação deste cliente, quando existe (a chave é omitida
  // quando não há; nunca vem null ou ""). É sugestão de preenchimento: não substitui
  // option/required/pattern/title, e o POST /valida segue obrigatório para todos os
  // blocos. O tipo acompanha o campo — ver applyHistoricoPrefill.
  historico?: string | number | boolean;
}

export interface DigitacaoFormResponse {
  id_cadastro: string;
  cpf_cliente: string;
  data_inicio: string;
  financeira: string;
  formulario: DigitacaoFormField[];
  status_validacao: Record<string, boolean>;
}

export interface DigitacaoValidationFieldError {
  field: string;
  message: string;
  type: string;
}

// Retorno de simulation/digitations/list — usado na Consulta de Propostas (por data,
// CPF ou número/financeira), ver SimulationBank.tsx.
export interface PropostaConsultaTimelineEntry {
  data: string;
  mensagem: string | null;
  origem: string;
  status: string;
}

// Só vem preenchido quando quem digitou foi um dependente (agente.tipo === "dependente");
// quando o próprio master digita, a esteira devolve digitador null e o código dele fica
// em agente.codigo_indicado — ver propostaDigitador em SimulationBank.tsx.
export interface PropostaConsultaDigitador {
  dependent_id: number;
  nome: string;
}

// codigo_indicado é o agente comissionado apontado na proposta: o próprio master quando
// ele digita, ou o operador informado na efetivação de uma digitação feita por um
// não comissionado (typing_agent_id).
export interface PropostaConsultaAgente {
  codigo_indicado: string | null;
  tipo: string | null;
  parceiro: { codigo: string | null; nome: string | null } | null;
}

export interface PropostaConsultaItem {
  id: number;
  proposal_number: string;
  client_name: string;
  cpf: string;
  // Quem digitou a proposta, exibido na coluna "Digitador" (ver AI-1158).
  digitador: PropostaConsultaDigitador | null;
  agente: PropostaConsultaAgente | null;
  financeira: string;
  financeira_id: string;
  bancarizadora: string | null;
  status: string;
  status_updated_at: string;
  created_at: string;
  produto: string;
  prazo: number | null;
  valor_liquido: number | null;
  valor_total: number | null;
  iof: number | null;
  seguro: number | null;
  tabela: string | null;
  formalization_url: string | null;
  journey_id: string;
  linha_do_tempo: PropostaConsultaTimelineEntry[];
}

// Retorno de simulation/digitations/partner-commission — usado antes da efetivação (step 7,
// StepConfirmacao) pra checar se o parceiro logado (ou um terceiro informado) é comissionado.
export interface PartnerCommissionResult {
  encontrado: boolean;
  comissionado: boolean;
  codigo_parceiro: string;
  codigo: number;
  nome: string;
  tipo_agente: number;
  tipo_nome: string;
  tipo_ativo: boolean;
}

export type PropostaResultado =
  | {
      status: 'EFETIVADO';
      id_cadastro: string;
      mensagem: string;
      data_efetivacao: string;
      resultados: Array<{
        financeira: string;
        sucesso: boolean;
        codigo_proposta: string;
        status_financeira: string;
        assinatura: {
          metodo: string;
          link: string;
          mensagem: string;
        };
        aviso?: string;
        tabela_comissao?: string;
      }>;
    }
  | {
      status: 'erro';
      mensagem: string;
    };
